import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileDropzone } from "../../../../components/Dropzone";
import { useTranslation } from "../../../../hooks/useTranslation";
import {
  DraftAlbum,
  Genre,
  MultiTrackTableItem,
  TrackFinancialModel,
  TrackReleaseSet,
  TrackServiceModel,
  useMusicApi
} from "../../../../api/useMusicApi";
import { UpdatedFields, UpdatedFieldsCallback, UpdateFields } from "../../../../hooks/use-advanced-state";
import { PartialNullable } from "../../../../types";
import { TrackItem } from "./TrackItem";
import { Button } from "../../../../components/Button";
import { useDeepCompareCallback } from "../../../../hooks/use-deep-compare-effect";
import { changeArrayPosition, getArtistsWithMoreThatOneUniqueUrl } from "../../../../utils";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompactDisc } from "@fortawesome/free-solid-svg-icons/faCompactDisc";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useDrop } from "react-dnd";
import cn from "classnames";
import _ from "lodash";
import { AxiosError } from "axios";
import { useValidationErrors } from "../../../../hooks/use-validation-errors";
import { useLoading } from "../../../../hooks/use-loading";
import { SaveFormButton } from "../../../../components/SaveFormButton";
import { BrowserScrollZone } from "./BrowserScrollZone";
import { TrackServiceType } from "./financialModels/TrackFinancialModels";
import { useModalConfirmAction } from "../../../../components/Modals/ModalConfirmActionContext";
import { prepareArtistsToRequest } from "../../../../services/ArtistService";

function onlyUnique(value: any, index: number, array: Array<any>) {
  return array.indexOf(value) === index;
}

function isUnfilledModel(financialModel: TrackFinancialModel) {
  return Object.keys(financialModel)
    .filter((service) => {
      return ![TrackServiceType.BUNDLE, TrackServiceType.BLOCK].includes(
        service as TrackServiceType
      );
    })
    .filter((service) => {
      const value = financialModel[
        service as keyof typeof financialModel
        ] as TrackServiceModel;
      return _.isEmpty(value.countries);
    });
}

type PropsDnd = {
  index: number;
  album: PartialNullable<DraftAlbum>;
  className?: string;
  onTrackMoveToDisc: (trackId: number | string, discIndex: number) => void;
  onDiscDelete: (discIndex: number) => void;
};
const DnDZoneForDisk: React.FC<PropsDnd> = ({
                                              index,
                                              className,
                                              onTrackMoveToDisc
                                            }) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop({
    accept: "track",
    drop: (item) => {
      // @ts-ignore
      onTrackMoveToDisc((item!.item! as TrackReleaseSet).id, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: true
    })
  });

  return (
    <div
      ref={drop}
      className={cn(
        "flex flex-col justify-center align-items-center rounded text-muted",
        className
      )}
      style={{
        height: 80,
        border: "1px dashed gray",
        opacity: isOver ? 0.5 : 1
      }}
    >
      <div>
        {t("add_track_to_disc_description")}{" "}
        <span className={"font-bold"}>
          {t("disc")} №{index}
        </span>
      </div>
    </div>
  );
};

export const MainTracksInformation: React.FC<{
  isDraft: boolean;
  album: PartialNullable<DraftAlbum>;
  genres: Genre[];
  onChange: UpdateFields<PartialNullable<DraftAlbum>>;
  onSave?: (tracks: PartialNullable<MultiTrackTableItem>[]) => Promise<any>;
}> = ({ album, genres, isDraft, onChange, onSave }) => {

  const { t } = useTranslation();
  const { showConfirmActionModal } = useModalConfirmAction();
  const dropZoneRef = useRef<HTMLInputElement>(null);
  const [replaceTrackId, setReplaceTrackId] = useState<string | number | null>(
    null
  );

  const { startLoading, stopLoading } = useLoading({ save: false });
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { validationErrors, setValidationErrors } = useValidationErrors(
    album,
    "trackrelease_set"
  );
  const [disks, setDisks] = useState<any[]>(
    album?.trackrelease_set
      ?.map((t) => t.album_part)
      .filter(onlyUnique)
      .map(() => ({})) ?? []
  );

  const { postUpdateMultiTrack } = useMusicApi();

  const handleReplaceFile = useCallback((trackId: string | number) => {
    if (dropZoneRef.current == null) {
      return;
    }

    setReplaceTrackId(trackId);
    setTimeout(() => dropZoneRef.current?.click(), 0);
  }, []);

  const handleMoveTrack = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      onChange((prevState) => {
        return {
          ...prevState,
          trackrelease_set: changeArrayPosition(
            dragIndex,
            hoverIndex,
            prevState?.trackrelease_set ?? []
          )
        };
      });
    },
    []
  );

  const handleMoveTrackToDisc = useCallback(
    (trackId: number | string, discIndex: number) => {
      onChange((prevState) => {
        const trackIndex = prevState.trackrelease_set?.findIndex(
          (t) => t.id == trackId
        );

        if (trackIndex == null || trackIndex === -1) {
          throw new Error();
        }

        if (
          discIndex == (prevState.trackrelease_set ?? [])[trackIndex].album_part
        ) {
          return prevState;
        }

        return {
          ...prevState,
          trackrelease_set: [
            ...(prevState.trackrelease_set ?? []).slice(0, trackIndex),
            {
              ...((prevState.trackrelease_set ?? [])[trackIndex] ?? {}),
              album_part: discIndex
            },
            ...(prevState.trackrelease_set ?? []).slice(trackIndex + 1)
          ]
        };
      });
    },
    []
  );


  const handleSave = useDeepCompareCallback(async () => {
    if (album != null && onSave != null) {
      startLoading("save");
      // @ts-ignore
      const data: PartialNullable<MultiTrackTableItem>[] =
        Object.values(_.groupBy(album?.trackrelease_set ?? [], "album_part"))
          .map((discTracks) => {
            return discTracks.map((t, index) => {
              return {
                id: t.id.toString().includes("uploaded_") ? undefined : +t.id,
                external_id: t.external_id,
                code_isrc: t.code_isrc,
                country_meta:
                  t.country_meta != null
                    ? {
                      ...t.country_meta,
                      artists: prepareArtistsToRequest(t?.country_meta ?? null),
                      start_point:
                        t.country_meta?.start_point != null &&
                        _.isFinite(+t.country_meta.start_point)
                          ? +t.country_meta.start_point
                          : t.country_meta.start_point
                    }
                    : {},
                album: album.id,
                genres: t.genres ?? [],
                lyric: t.lyric,
                composer: t.composer,
                is_explicit: t.is_explicit,
                right_author_percentage: t.right_author_percentage,
                services: t.services,
                file_path_b64: t.file_path_b64,
                focus: t.focus,
                track_number: index + 1,
                album_part: t.album_part != null ? +t.album_part : undefined
              };
            });
          })
          .flat() ?? [];

      const unfilledFinancialModels = data
        .map((track) => {
          const financialModel = track.services;
          if (financialModel == null) {
            return true;
          }

          return [track, isUnfilledModel(financialModel)];
        })
        .filter((value) => {
          const [_, unfilled]: [MultiTrackTableItem, string[]] = value as any;
          return (
            unfilled.length ===
            [
              TrackServiceType.GIFT,
              TrackServiceType.SUBSCRIPTION,
              TrackServiceType.DOWNLOAD,
              TrackServiceType.STREAMING
            ].length
          );
        });

      if (unfilledFinancialModels.length > 0) {
        const description =
          t("unfilled_financial_models_warning_description") +
          "\n\n" +
          unfilledFinancialModels
            .map((value) => {
              const [track]: [MultiTrackTableItem, string[]] = value as any;
              return "— " + track.country_meta.title["ALL"];
            })
            .join("\n");

        const closeModalResult = await showConfirmActionModal(
          t("unfilled_financial_models_warning"),
          description
        );

        if (!closeModalResult) {
          return;
        }
      }

      const invalidMainArtistsEntries = getArtistsWithMoreThatOneUniqueUrl(t, album, data);
      if (invalidMainArtistsEntries.length > 0) {
        const description = invalidMainArtistsEntries.map(([key, values]) => {
          let title = `${key}: \n`;
          (values as any).forEach((v: any) => {
            title += `${v.track} – ${v.url}\n`;
          });
          return title;
        }).join("\n\n");

        const closeModalResult = await showConfirmActionModal(
          t("artists_equals_url_error"),
          description
        );

        if (!closeModalResult) {
          return;
        }
      }


      try {
        await onSave(data);
        setValidationErrors([], album);
      } catch (e) {
        setValidationErrors(
          ((e as AxiosError).response?.data as any)?.errors ?? [],
          album
        );
      } finally {
        stopLoading("save");
      }
    }
  }, [album?.trackrelease_set]);

  const handleTrackDelete = useCallback(
    (trackId: number | string, fixedIndex: number) => {
      onChange((prevState) => {
        const index = prevState.trackrelease_set?.findIndex(
          (t) => t.id == trackId
        );

        if (index == null || index === -1) {
          return prevState;
        }

        const updatedErrors = validationErrors.filter(
          (e) => e.attr.split(".")[0] != fixedIndex.toString()
        );

        const updatedAlbum = {
          ...prevState,
          trackrelease_set: [
            ...(prevState.trackrelease_set ?? []).slice(0, index),
            ...(prevState.trackrelease_set ?? []).slice(index + 1)
          ]
        };

        setValidationErrors(updatedErrors, updatedAlbum);

        return updatedAlbum;
      });
    },
    [validationErrors]
  );

  const handleDiscDelete = useCallback((index: number) => {
    setDisks((prevState) => {
      return [
        ...(prevState ?? []).slice(0, index),
        ...(prevState ?? []).slice(index + 1)
      ];
    });
  }, []);

  const handleTrackUpdate = useCallback(
    (
      trackId: number | string,
      data:
        | UpdatedFields<TrackReleaseSet>
        | UpdatedFieldsCallback<TrackReleaseSet>
    ) => {
      onChange((prevState) => {
        const prevTrackStateIndex =
          prevState?.trackrelease_set?.findIndex((t) => {
            return t.id === trackId;
          }) ?? -1;

        if (prevTrackStateIndex === -1) {
          return prevState;
        }

        const prevTrackState =
          prevState.trackrelease_set?.[prevTrackStateIndex] ?? {};

        const updatedTrackReleaseItem = {
          ...prevTrackState,
          ...(data instanceof Function ? data(prevTrackState) : data)
        } as TrackReleaseSet;

        return {
          ...prevState,
          trackrelease_set: [
            ...(prevState.trackrelease_set?.slice(0, prevTrackStateIndex) ??
              []),
            updatedTrackReleaseItem,
            ...(prevState.trackrelease_set?.slice(prevTrackStateIndex + 1) ??
              [])
          ]
        };
      });
    },
    [onChange, album]
  );

  const handleTrackUpdateForAll = useCallback(
    (trackId: number | string, field: keyof TrackReleaseSet | string) => {
      onChange((prevState) => {
        const prevTrackStateIndex =
          prevState?.trackrelease_set?.findIndex((t) => {
            return t.id === trackId;
          }) ?? -1;

        if (prevTrackStateIndex === -1) {
          return prevState;
        }

        const prevTrackState = prevState.trackrelease_set?.[prevTrackStateIndex] ?? null;
        if (prevTrackState == null) {
          throw new Error();
        }

        const prevAllTracks = [...(prevState?.trackrelease_set ?? [])];
        const editedTracks = prevAllTracks.map((t) => {
          return _.set(t, field, _.cloneDeep(_.get(prevTrackState, field)));
        });

        toast.success(t("success_copy_field"));
        return {
          ...prevState,
          trackrelease_set: [...(editedTracks ?? [])]
        };
      });
    },
    []
  );

  useEffect(() => {
    if (replaceTrackId == null) {
      const uploadedFilesTrackReleaseSet = uploadedFiles.map(
        (f: File): PartialNullable<TrackReleaseSet> => {
          let fileNameWithoutExtension: string;
          const fileNameParts = f.name.split(".");
          if (fileNameParts.length === 2) {
            fileNameWithoutExtension = f.name.split(".")[0];
          } else {
            fileNameWithoutExtension = fileNameParts.slice(0, fileNameParts.length - 1).join(".");
          }

          return {
            id: `uploaded_${f.name}_${Date.now()}`,
            file: null,
            file_name: f.name,
            country_meta: {
              title: {
                ALL: fileNameWithoutExtension ?? null,
                RU: null
              },
              subtitle: {
                ALL: null,
                RU: null
              },
              artists: album?.country_meta?.artists ?? {
                main: [],
                featured: []
              },
              start_point: null
            },
            genres: album?.genres ?? [],
            services: {
              SUBSCRIPTION: {
                clip: false,
                story: false,
                countries: {}
              },
              STREAMING: {
                clip: false,
                story: false,
                countries: {}
              },
              DOWNLOAD: { countries: {} },
              GIFT: { countries: {} },
              BLOCK: { countries: {} },
              BUNDLE: { countries: {} }
            },
            album_part: 1
          };
        }
      );

      if (uploadedFilesTrackReleaseSet.length === 0) {
        return;
      }

      if (disks.length === 0) {
        setDisks((prev) => [...prev, {}]);
      }

      onChange(
        (prevState) =>
          ({
            ...prevState,
            trackrelease_set: [
              ...(prevState.trackrelease_set ?? []),
              ...(uploadedFilesTrackReleaseSet ?? [])
            ]
          }) as any
      );
    } else {
      onChange((prevState) => {
        const prevTrackStateIndex =
          prevState?.trackrelease_set?.findIndex((t) => {
            return t.id === replaceTrackId;
          }) ?? -1;

        if (prevTrackStateIndex === -1) {
          return prevState;
        }

        const prevTrackState =
          prevState.trackrelease_set?.[prevTrackStateIndex] ?? null;
        if (prevTrackState == null) {
          throw new Error();
        }

        prevTrackState.file_name = uploadedFiles[uploadedFiles.length - 1].name;

        return {
          ...prevState,
          trackrelease_set: [
            ...(prevState.trackrelease_set?.slice(0, prevTrackStateIndex) ??
              []),
            { ...(prevTrackState as any) },
            ...(prevState.trackrelease_set?.slice(prevTrackStateIndex + 1) ??
              [])
          ]
        } as any;
      });
    }
  }, [uploadedFiles]);

  return (
    <div className={"flex flex-col w-full border-solid p-3 border-2 border-gray-200 rounded-r rounded-b"}>
      {isDraft && (
        <>
          <br />
          <FileDropzone
            // @ts-ignore
            ref={dropZoneRef}
            accept={
              (album?.content_type ?? "audio") === "audio"
                ? {
                  "audio/mp3": [".mp3"],
                  "audio/wav": [".wav"]
                }
                : {
                  "video/mp4": [".mp4"],
                  "video/avi": [".avi"],
                  "video/mov": [".mov"]
                }
            }
            onFileChange={setUploadedFiles}
          />
          <div className={"my-3"} />
        </>
      )}

      <BrowserScrollZone direction={"top"} visible={isDragging} />

      {disks.map((_, diskIndex) => {
        const needShowDropPartZone = disks.length > 1;
        return (
          <React.Fragment key={diskIndex}>
            <div className={"font-bold text-muted my-4"}>
              <FontAwesomeIcon icon={faCompactDisc} size={"xl"} /> {t("disc")} №
              {diskIndex + 1}{" "}
              {album?.trackrelease_set?.find(
                (t) => t.album_part == diskIndex + 1
              ) == null && (
                <FontAwesomeIcon
                  icon={faClose}
                  className={"text-danger cursor-pointer"}
                  onClick={() => handleDiscDelete(diskIndex)}
                />
              )}
            </div>
            {diskIndex !== 0 && needShowDropPartZone && isDragging && (
              <DnDZoneForDisk
                index={diskIndex + 1}
                album={album}
                className={"my-3"}
                onDiscDelete={handleDiscDelete}
                onTrackMoveToDisc={handleMoveTrackToDisc}
              />
            )}
            {album?.trackrelease_set
              ?.filter((t) => t.album_part == diskIndex + 1)
              .map((trackRelease, index) => {
                const uploadedFilesCount = uploadedFiles.filter(() => {
                  return (
                    trackRelease.id?.toString().includes("uploaded_") ?? false
                  );
                }).length;
                const linkedFile = uploadedFiles.find((f) => {
                  return (
                    f.name === trackRelease.file_name?.replace("uploaded_", "")
                  );
                });

                const fixedIndex =
                  (album.trackrelease_set ?? []).filter(
                    (t) => t.album_part < diskIndex + 1
                  ).length + index;

                return (
                  <TrackItem
                    key={trackRelease.id}
                    isDraft={isDraft}
                    index={fixedIndex}
                    validationErrors={validationErrors}
                    item={trackRelease}
                    album={album}
                    isInitExpanded={uploadedFilesCount == 1}
                    file={linkedFile}
                    genres={genres}
                    onMove={handleMoveTrack}
                    className={"mb-2"}
                    onDragging={setIsDragging}
                    onDelete={() =>
                      handleTrackDelete(trackRelease.id, fixedIndex)
                    }
                    onReloadFile={handleReplaceFile}
                    onChange={(data) =>
                      handleTrackUpdate(trackRelease.id, data as any)
                    }
                    onChangeForAll={(field) =>
                      handleTrackUpdateForAll(trackRelease.id, field)
                    }
                  />
                );
              })}

            {diskIndex === 0 && needShowDropPartZone && isDragging && (
              <DnDZoneForDisk
                index={diskIndex + 1}
                album={album}
                className={"my-3"}
                onDiscDelete={handleDiscDelete}
                onTrackMoveToDisc={handleMoveTrackToDisc}
              />
            )}
          </React.Fragment>
        );
      })}

      {isDraft && (
        <div className={"flex justify-center my-4"}>
          <Button
            label={t("add_disc").toString()}
            icon={faCompactDisc}
            onClick={() => setDisks((prev) => [...prev, {}])}
          />
        </div>
      )}

      {isDraft && (
        <SaveFormButton
          validationErrors={validationErrors}
          onClick={handleSave}
        />
      )}

      <BrowserScrollZone direction={"bottom"} visible={isDragging} />
    </div>
  );
};
